import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Helmet } from 'react-helmet-async';
import { fetchProducts } from '@/services/productService';
import { fetchCategories, fetchSubcategories } from '@/services/categoryService';
import { Skeleton } from '@/components/ui/skeleton';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Category, Subcategory } from '@/types/category';
import { useLanguage } from '@/context/LanguageContext';

const PAGE_SIZE = 9;

const CategoryPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { tl } = useLanguage();
  const [subcategories, setSubcategories] = useState<Subcategory[]>([]);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  const { data: allCategories } = useQuery({
    queryKey: ['categories'],
    queryFn: fetchCategories,
  });

  const findCategory = (categoryIdentifier: string, categories: Category[]) => {
    const exactMatch = categories?.find(
      (cat) => cat.name.toLowerCase() === categoryIdentifier.toLowerCase()
    );
    if (exactMatch) return exactMatch;

    const normalizedIdentifier = categoryIdentifier.toLowerCase().replace(/-/g, ' ');
    return categories?.find(
      (cat) =>
        cat.name.toLowerCase() === normalizedIdentifier ||
        cat.slug?.toLowerCase() === categoryIdentifier.toLowerCase()
    );
  };

  const category = allCategories ? findCategory(id as string, allCategories) : null;

  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [category?.id]);

  useEffect(() => {
    const loadSubcategories = async () => {
      if (!category) return;
      try {
        setSubcategories(await fetchSubcategories(category.id));
      } catch (error) {
        console.error('Error loading subcategories:', error);
      }
    };
    loadSubcategories();
  }, [category]);

  const {
    data: products = [],
    isLoading: productsLoading,
    error: productsError,
  } = useQuery({
    queryKey: ['categoryProducts', category?.name],
    queryFn: async () => {
      if (!category) return [];
      return fetchProducts({
        categories: [category.name],
        sort: 'newest',
      });
    },
    enabled: !!category,
  });

  const sortedProducts = useMemo(
    () => [...products].sort((a, b) => a.name.localeCompare(b.name)),
    [products]
  );

  const visibleProducts = sortedProducts.slice(0, visibleCount);
  const hasMore = visibleCount < sortedProducts.length;

  const metaTitle = category ? `${category.name} | Meubles Karim` : 'Catégorie | Meubles Karim';
  const metaDescription = category
    ? `Explorez notre collection de ${category.name}. Des meubles de qualité pour votre maison chez Meubles Karim.`
    : 'Parcourez nos collections de meubles par catégorie chez Meubles Karim.';

  if (productsLoading || (allCategories && !category)) {
    return <CategorySkeleton />;
  }

  if (productsError || !category) {
    return (
      <div className="container-custom bg-white py-16 text-center">
        <h2 className="mb-4 text-2xl font-medium text-black">
          {tl('Catégorie Introuvable', 'Category Not Found', 'Categoria non trovata')}
        </h2>
        <p className="mb-8 text-black text-muted-foreground">
          {tl(
            "La catégorie que vous recherchez n'existe pas ou a été supprimée.",
            'The category you are looking for does not exist or has been removed.',
            'La categoria che state cercando non esiste o è stata rimossa.'
          )}
        </p>
        <Link
          to="/"
          className="inline-flex items-center text-furniture-taupe hover:text-furniture-brown"
        >
          <ArrowLeft size={16} className="mr-2" />
          {tl("Retour à l'accueil", 'Back to Home', 'Torna alla home')}
        </Link>
      </div>
    );
  }

  return (
    <main className="bg-white text-black">
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDescription} />
      </Helmet>

      <section className="bg-white pb-8 pt-[88px] md:pb-10 lg:pt-[104px]">
        <h1 className="text-center text-xl font-semibold uppercase leading-none tracking-[0.18em] text-black md:text-2xl">
          {category.name}
        </h1>
      </section>

      <div className="container mx-auto bg-white px-4 pb-20 text-black">
        {sortedProducts.length === 0 ? (
          <div className="py-16 text-center">
            <p className="text-lg text-muted-foreground">
              {tl(
                'Aucun produit trouvé dans cette catégorie.',
                'No products found in this category.',
                'Nessun prodotto trovato in questa categoria.'
              )}
            </p>
            <Button variant="outline" className="mt-4 rounded-none" asChild>
              <Link to="/products">
                {tl(
                  'Parcourir tous les produits',
                  'Browse all products',
                  'Sfoglia tutti i prodotti'
                )}
              </Link>
            </Button>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 gap-x-6 gap-y-12 sm:grid-cols-2 lg:grid-cols-3">
              {visibleProducts.map((product) => {
                const primaryImage =
                  product.images?.[0] || product.image_nobg || '/placeholder.svg';
                const secondaryImage =
                  product.images?.[1] && product.images[1] !== primaryImage
                    ? product.images[1]
                    : product.image_nobg && product.image_nobg !== primaryImage
                      ? product.image_nobg
                      : null;

                return (
                  <article
                    key={product.id}
                    className="group cursor-pointer"
                    onClick={() => navigate(`/product/${product.id}`)}
                  >
                    <div className="relative aspect-square w-full overflow-hidden bg-neutral-100">
                      <img
                        src={primaryImage}
                        alt={product.name}
                        className="h-full w-full object-cover transition-opacity duration-500 ease-out"
                      />
                      {secondaryImage && (
                        <img
                          src={secondaryImage}
                          alt=""
                          aria-hidden
                          className="absolute inset-0 h-full w-full object-cover opacity-0 transition-opacity duration-500 ease-out group-hover:opacity-100"
                        />
                      )}
                      <div className="absolute left-0 top-0 flex flex-col items-start gap-1">
                        {product.discount != null && product.discount > 0 && (
                          <span className="bg-red-600 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-white">
                            -{product.discount}%
                          </span>
                        )}
                        {product.new && (
                          <span className="bg-neutral-800 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-white">
                            {tl('Nouveau', 'New', 'Nuovo')}
                          </span>
                        )}
                        {product.collection_2026 && (
                          <span className="bg-furniture-gold px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-black">
                            {tl('Collection 2026', '2026 Collection', 'Collezione 2026')}
                          </span>
                        )}
                        {product.featured && (
                          <span className="bg-furniture-wine px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-white">
                            {tl('Tendance', 'In Trend', 'Di tendenza')}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="mt-4 text-left">
                      <h3 className="text-sm font-semibold uppercase tracking-[0.12em] text-black">
                        {product.name}
                      </h3>
                      <p className="mt-1 text-xs text-neutral-500">
                        {product.subcategory
                          ? subcategories.find((s) => s.id === product.subcategory)?.name ||
                            product.category
                          : product.category}
                      </p>
                    </div>
                  </article>
                );
              })}
            </div>

            {hasMore && (
              <div className="mt-16 flex justify-center">
                <button
                  type="button"
                  onClick={() => setVisibleCount((count) => count + PAGE_SIZE)}
                  className="text-xs font-medium uppercase tracking-[0.18em] text-black underline underline-offset-8 transition-opacity hover:opacity-55"
                >
                  {tl('Voir plus', 'See more', 'Vedi di più')}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </main>
  );
};

const CategorySkeleton = () => {
  return (
    <>
      <div className="bg-white pb-8 pt-[88px] md:pb-10 lg:pt-[104px]">
        <Skeleton className="mx-auto h-6 w-36 rounded-none" />
      </div>

      <div className="container-custom py-12">
        <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="space-y-4">
              <Skeleton className="aspect-square w-full rounded-none" />
              <Skeleton className="h-4 w-2/3 rounded-none" />
              <Skeleton className="h-3 w-1/3 rounded-none" />
            </div>
          ))}
        </div>
      </div>
    </>
  );
};

export default CategoryPage;
